import React, { useState, useMemo, useCallback } from 'react'
import './Table.css'

export interface TableColumn<T> {
  /** Unique key for the column */
  key: string
  /** Column header text */
  header: string
  /** Custom render function for cell content */
  render?: (row: T, index: number) => React.ReactNode
  /** Enable sorting for this column */
  sortable?: boolean
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  /** Column width (CSS value) */
  width?: string
  /** Min width for responsive */
  minWidth?: string
}

export interface TableProps<T> {
  /** Column definitions */
  columns: TableColumn<T>[]
  /** Data rows */
  data: T[]
  /** Function to extract unique key from each row */
  keyExtractor: (row: T, index: number) => string
  /** Message shown when data is empty */
  emptyMessage?: string
  /** Show loading state */
  loading?: boolean
  /** Row click handler */
  onRowClick?: (row: T, index: number) => void
  /** Additional CSS class */
  className?: string
  /** Enable hover effect on rows */
  hoverable?: boolean
  /** Enable striped rows */
  striped?: boolean
  /** Compact mode with less padding */
  compact?: boolean
  /** Sticky header */
  stickyHeader?: boolean
}

type SortDirection = 'asc' | 'desc' | null

export function Table<T>({
  columns,
  data,
  keyExtractor,
  emptyMessage = 'Keine Daten vorhanden',
  loading = false,
  onRowClick,
  className = '',
  hoverable = true,
  striped = false,
  compact = false,
  stickyHeader = false,
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortKey(null)
        setSortDirection(null)
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }, [sortKey, sortDirection])

  const sortedData = useMemo(() => {
    if (!sortKey || !sortDirection) {
      return data
    }

    return [...data].sort((a, b) => {
      const aValue = (a as Record<string, unknown>)[sortKey]
      const bValue = (b as Record<string, unknown>)[sortKey]

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.localeCompare(bValue, 'de')
        return sortDirection === 'asc' ? result : -result
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return sortDirection === 'asc' 
          ? aValue.getTime() - bValue.getTime() 
          : bValue.getTime() - aValue.getTime()
      }

      const result = String(aValue).localeCompare(String(bValue), 'de')
      return sortDirection === 'asc' ? result : -result
    })
  }, [data, sortKey, sortDirection])

  const tableClasses = [
    'shared-table',
    hoverable && 'hoverable',
    striped && 'striped',
    compact && 'compact',
    stickyHeader && 'sticky-header',
    className,
  ].filter(Boolean).join(' ')

  const getCellAlignment = (align?: string) => {
    switch (align) {
      case 'center': return 'text-center'
      case 'right': return 'text-right'
      default: return 'text-left'
    }
  }

  const getSortIcon = (key: string) => {
    if (sortKey !== key) {
      return <span className="sort-icon sort-icon-inactive">⇅</span>
    }
    if (sortDirection === 'asc') {
      return <span className="sort-icon sort-icon-active">↑</span>
    }
    if (sortDirection === 'desc') {
      return <span className="sort-icon sort-icon-active">↓</span>
    }
    return <span className="sort-icon sort-icon-inactive">⇅</span>
  }

  if (loading) {
    return (
      <div className="table-wrapper">
        <div className="table-loading">
          <div className="table-loading-spinner" />
          <span>Daten werden geladen...</span>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="table-wrapper">
        <div className="table-empty">
          <span className="table-empty-icon">📭</span>
          <p className="table-empty-message">{emptyMessage}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="table-wrapper">
      <table className={tableClasses}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`${getCellAlignment(column.align)} ${column.sortable ? 'sortable' : ''}`}
                style={{ 
                  width: column.width, 
                  minWidth: column.minWidth 
                }}
                onClick={column.sortable ? () => handleSort(column.key) : undefined}
                aria-sort={
                  sortKey === column.key 
                    ? sortDirection === 'asc' ? 'ascending' : 'descending'
                    : undefined
                }
              >
                <span className="th-content">
                  {column.header}
                  {column.sortable && getSortIcon(column.key)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIndex) => (
            <tr
              key={keyExtractor(row, rowIndex)}
              onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
              className={onRowClick ? 'clickable' : ''}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onRowClick(row, rowIndex)
                }
              } : undefined}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={getCellAlignment(column.align)}
                  style={{ 
                    width: column.width, 
                    minWidth: column.minWidth 
                  }}
                >
                  {column.render 
                    ? column.render(row, rowIndex) 
                    : String((row as Record<string, unknown>)[column.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default Table
